import React, { useReducer, type ReactElement } from "react";
import { Alert, AlertDescription, AlertTitle, Button, Input, cn } from "@ndd/ui";
import { TriangleAlert } from "lucide-react";
import type { QuickFactsAndGoalsListResponse } from "../../../../shared/api-types";
import { THEMED_CARD, THEMED_CARD_TITLE } from "../../lib/always-look";
import {
  AT_CAP_MESSAGE,
  CONFLICT_TITLE,
  EMPTY_LIST_MESSAGE,
  LIST_COPY,
  MAX_ITEM_LENGTH,
  READ_STATE,
  SAVE_FAILED_MESSAGE,
  canAddItem,
  listEditorReducer,
  normalizeDraft,
  saveBase,
  type ListEditorAction,
  type ListEditorState,
  type ListKind,
  type ListSaveResult,
} from "../../lib/quick-facts-and-goals-client";

// Quick facts and Goals (NO-852): two plain cards in the patient page's right
// column, styled like the Appointments card. A leaf module (`import React`,
// relative imports, no router, no queries, no telemetry) so `tsx --test`
// renders it. The page supplies each card's load state and its save; list
// text is shown and sent, never logged.

export type ListCardLoad =
  | { status: "ready"; list: QuickFactsAndGoalsListResponse }
  | { status: "failed"; onRetry: () => void };

export interface ListCardProps {
  kind: ListKind;
  load: ListCardLoad;
  canEdit: boolean;
  onSave: (items: string[], baseVersion: number) => Promise<ListSaveResult>;
  /** Tests only: the editor's starting state. */
  initialState?: ListEditorState;
}

const CARD = cn("rounded-xl border border-border bg-card px-7 py-6", THEMED_CARD);

const CARD_HEADING = cn(
  "font-sans text-body-lg font-semibold text-foreground",
  THEMED_CARD_TITLE
);

const NEUTRAL_LINE = "text-body text-muted-foreground";

const QUIET_BUTTON = "h-auto px-0 py-0 text-body";

/** One list's card: read view, edit view, save and conflict. */
export function ListCard({
  kind,
  load,
  canEdit,
  onSave,
  initialState = READ_STATE,
}: ListCardProps): ReactElement {
  const [state, dispatch] = useReducer(listEditorReducer, initialState);
  const copy = LIST_COPY[kind];

  async function save(saveMine: boolean): Promise<void> {
    const base = saveBase(state, saveMine);
    dispatch({ type: "save_started" });
    const result = await onSave(normalizeDraft(state.draft), base);
    dispatch(
      result.status === "conflict"
        ? { type: "conflict", current: result.current }
        : { type: result.status }
    );
  }

  return (
    <section aria-label={copy.title} data-list-card={kind} className={CARD}>
      <h2 className={CARD_HEADING}>{copy.title}</h2>
      <div className="mt-4">
        {load.status === "failed" ? (
          <LoadFailed message={copy.loadFailed} onRetry={load.onRetry} />
        ) : state.editing ? (
          <EditView
            kind={kind}
            state={state}
            dispatch={dispatch}
            onSave={() => void save(false)}
            onSaveMine={() => void save(true)}
          />
        ) : (
          <ReadView
            kind={kind}
            list={load.list}
            canEdit={canEdit}
            onEdit={() => dispatch({ type: "edit", list: load.list })}
          />
        )}
      </div>
    </section>
  );
}

function LoadFailed({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): ReactElement {
  return (
    <div className="flex flex-col items-start gap-3">
      <p className={NEUTRAL_LINE}>{message}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

function ReadView({
  kind,
  list,
  canEdit,
  onEdit,
}: {
  kind: ListKind;
  list: QuickFactsAndGoalsListResponse;
  canEdit: boolean;
  onEdit: () => void;
}): ReactElement {
  const empty = list.items.length === 0;

  return (
    <>
      {empty ? <p className={NEUTRAL_LINE}>{EMPTY_LIST_MESSAGE}</p> : null}
      {!empty ? <ListItems kind={kind} items={list.items} /> : null}
      {canEdit ? (
        <Button
          type="button"
          variant="quiet-link"
          className={cn("mt-4", QUIET_BUTTON)}
          onClick={onEdit}
        >
          {empty ? "Add" : "Edit"}
        </Button>
      ) : null}
    </>
  );
}

/** Quick facts are a bulleted list; Goals are plain lines. */
function ListItems({ kind, items }: { kind: ListKind; items: string[] }): ReactElement {
  const bulleted = LIST_COPY[kind].bulleted;

  return (
    <ul
      className={cn(
        "flex flex-col gap-2 text-body text-foreground",
        bulleted && "list-disc pl-5"
      )}
    >
      {items.map((item, index) => (
        <li key={`${index}:${item}`}>{item}</li>
      ))}
    </ul>
  );
}

function EditView({
  kind,
  state,
  dispatch,
  onSave,
  onSaveMine,
}: {
  kind: ListKind;
  state: ListEditorState;
  dispatch: (action: ListEditorAction) => void;
  onSave: () => void;
  onSaveMine: () => void;
}): ReactElement {
  const copy = LIST_COPY[kind];

  return (
    <div className="flex flex-col gap-3">
      {state.conflict ? (
        <ConflictAlert
          current={state.conflict}
          disabled={state.saving}
          onUseLatest={() => dispatch({ type: "use_latest" })}
          onSaveMine={onSaveMine}
        />
      ) : null}
      {state.draft.map((item, index) => (
        <DraftItem
          key={index}
          label={`${copy.title} item ${index + 1}`}
          value={item}
          disabled={state.saving}
          onChange={(value) => dispatch({ type: "change", index, value })}
          onRemove={() => dispatch({ type: "remove", index })}
        />
      ))}
      {canAddItem(state.draft) ? (
        <Button
          type="button"
          variant="quiet-link"
          className={cn("self-start", QUIET_BUTTON)}
          disabled={state.saving}
          onClick={() => dispatch({ type: "add" })}
        >
          {copy.addItem}
        </Button>
      ) : (
        <p className={NEUTRAL_LINE}>{AT_CAP_MESSAGE}</p>
      )}
      {state.failed ? <p className={NEUTRAL_LINE}>{SAVE_FAILED_MESSAGE}</p> : null}
      <EditActions
        saving={state.saving}
        conflicted={state.conflict !== null}
        onSave={onSave}
        onCancel={() => dispatch({ type: "cancel" })}
      />
    </div>
  );
}

function DraftItem({
  label,
  value,
  disabled,
  onChange,
  onRemove,
}: {
  label: string;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
  onRemove: () => void;
}): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Input
        aria-label={label}
        value={value}
        maxLength={MAX_ITEM_LENGTH}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        className="min-w-0 flex-1"
      />
      <Button
        type="button"
        variant="quiet-link"
        className={QUIET_BUTTON}
        disabled={disabled}
        onClick={onRemove}
      >
        Remove
      </Button>
    </div>
  );
}

function EditActions({
  saving,
  conflicted,
  onSave,
  onCancel,
}: {
  saving: boolean;
  conflicted: boolean;
  onSave: () => void;
  onCancel: () => void;
}): ReactElement {
  return (
    <div className="mt-1 flex flex-wrap gap-2">
      <Button type="button" size="sm" disabled={saving || conflicted} onClick={onSave}>
        Save
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={saving}
        onClick={onCancel}
      >
        Cancel
      </Button>
    </div>
  );
}

/**
 * The editor conflict grammar (components/appointments/editor-status.tsx):
 * amber because losing an edit is a risk worth knowing. It shows the list as
 * it stands now and keeps the RD's draft until they choose.
 */
function ConflictAlert({
  current,
  disabled,
  onUseLatest,
  onSaveMine,
}: {
  current: QuickFactsAndGoalsListResponse;
  disabled: boolean;
  onUseLatest: () => void;
  onSaveMine: () => void;
}): ReactElement {
  return (
    <Alert variant="warning" data-list-conflict="">
      <TriangleAlert aria-hidden="true" />
      <AlertTitle>{CONFLICT_TITLE}</AlertTitle>
      <AlertDescription className="mt-1">
        {current.items.length === 0 ? (
          <p>{EMPTY_LIST_MESSAGE}</p>
        ) : (
          <ul className="list-disc pl-5">
            {current.items.map((item, index) => (
              <li key={`${index}:${item}`}>{item}</li>
            ))}
          </ul>
        )}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            onClick={onUseLatest}
          >
            Use latest
          </Button>
          <Button type="button" size="sm" disabled={disabled} onClick={onSaveMine}>
            Save mine instead
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}
